// The time-and-place picker's data (BOOKINGS_PLAN.md B7b): one GET
// bookings/scheduling per form, answering `active` (false = the club doesn't
// take bookings for this sport: the form keeps its old date, time and
// playing-area inputs exactly as they are) and the days → times → areas the
// picker lays out. Reloads for another window of days or an admin's one-off
// match length.
import {computed, ref} from 'vue';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints';
import {withSessionMoveStarts} from '~/utils/sessionMove';

export function useSchedulingAvailability() {
  const {httpRequest} = useHttpRequest();
  const availability = ref(null);
  const loading = ref(false);
  // null until the first answer: the forms show a placeholder, never the wrong input
  const active = ref(null);
  const failed = ref(false);
  let lastParams = null;
  let seq = 0;

  /**
   * @param params {clubId, sportId, fixtureId, ladderChallengeId, ladderId, leagueCycleId, tournamentId,
   *   americanoCycleId, homeTeamId, awayTeamId, from (yyyy-MM-dd), days, minutes (admin)}
   */
  const load = async (params) => {
    lastParams = {...(params ?? {})};
    const mine = ++seq;
    loading.value = true;
    failed.value = false;
    try {
      const res = await httpRequest(endpoints.bookings.scheduling(lastParams));
      if (mine !== seq) return availability.value;
      // X240 (TEST_R100 M-1): a shared americano session's own starts read free for the whole session's move.
      availability.value = withSessionMoveStarts(res ?? null);
      active.value = res?.bookingsActive === true;
      return res;
    } catch {
      if (mine !== seq) return availability.value;
      // an old API, a member of nothing, a blip: the old inputs are always right
      availability.value = null;
      active.value = false;
      failed.value = true;
      return null;
    } finally {
      if (mine === seq) loading.value = false;
    }
  };

  const reload = (changes = {}) => load({...(lastParams ?? {}), ...changes});
  const reset = () => {
    seq++;
    availability.value = null;
    active.value = null;
    loading.value = false;
    failed.value = false;
    lastParams = null;
  };

  const timeZone = computed(() => availability.value?.timeZone ?? null);

  return {availability, active, loading, failed, timeZone, load, reload, reset};
}
